import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/JanataDarbarTable.css";
import { useNavigate } from "react-router-dom";
import { fileAPI, janataDarbarAPI } from "../../api/index"; // adjust path to match your project
import { MANDALS } from "./JanataDarbarForm"; // reuse the same id->name list — keeps them in sync

const mandalName = (id) => MANDALS.find((m) => m.id === id)?.name || "—";

const STATUS_TO_LABEL = {
  pending: "Pending",
  in_progress: "In Progress",
  resolved: "Resolved",
  referred: "Referred",
};
const STATUS_TO_BACKEND = {
  Pending: "pending",
  "In Progress": "in_progress",
  Resolved: "resolved",
  Referred: "referred",
};

const AVATAR_COLORS = ["darb123-avatar-purple", "darb123-avatar-blue", "darb123-avatar-pink"];

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "?";

const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

const PAGE_SIZE = 10;

const JanataDarbarTable = () => {
  const navigate = useNavigate();

  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [mandalFilter, setMandalFilter] = useState("All Mandals");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- FETCH ----------
  const fetchVisitors = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await janataDarbarAPI.getAll({});
      // Backend returns a PaginatedResponse: { items, total, page, page_size, pages }
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setVisitors(data);
    } catch (err) {
      console.error("Failed to load visitors:", err);
      setLoadError(err.response?.data?.detail || "Could not load visitors.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisitors();
  }, [fetchVisitors]);

  const resetFilters = () => {
    setSearch("");
    setCategoryFilter("All Categories");
    setMandalFilter("All Mandals");
    setStatusFilter("All Status");
    setCurrentPage(1);
  };

  // ---------- SEARCH + FILTER (client-side) ----------
  const filteredVisitors = visitors.filter((item) => {
    const searchLower = search.trim().toLowerCase();

    const matchesSearch =
      searchLower === "" ||
      (item.visitor_name || "").toLowerCase().includes(searchLower) ||
      (item.token_number || "").toLowerCase().includes(searchLower) ||
      (item.mobile_number || "").includes(searchLower);

    const matchesCategory =
      categoryFilter === "All Categories" || item.issue_category === categoryFilter;

    const matchesMandal =
      mandalFilter === "All Mandals" || String(item.mandal_id) === mandalFilter;

    const statusLabel = STATUS_TO_LABEL[item.status] || item.status;
    const matchesStatus = statusFilter === "All Status" || statusLabel === statusFilter;

    return matchesSearch && matchesCategory && matchesMandal && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredVisitors.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const pageVisitors = filteredVisitors.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ---------- STATS ----------
  const totalVisitors = visitors.length;
  const resolvedCount = visitors.filter((v) => v.status === "resolved").length;
  const inProgressCount = visitors.filter((v) => v.status === "in_progress").length;
  const pendingCount = visitors.filter((v) => v.status === "pending").length;

  const getStatusClass = (statusLabel) => {
    switch (statusLabel) {
      case "Resolved":
        return "darb123-status-resolved";
      case "In Progress":
        return "darb123-status-progress";
      case "Pending":
        return "darb123-status-pending";
      case "Referred":
        return "darb123-status-referred";
      default:
        return "";
    }
  };

  const todayDisplay = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  // ---------- EDIT LOGIC ----------
  const openEditModal = (item) => {
    setEditItem({
      id: item.id,
      token: item.token_number || "", // read-only, auto-generated
      name: item.visitor_name || "",
      mobile: item.mobile_number || "",
      voterId: item.epic_no || "",
      age: item.age ?? "",
      gender: item.gender || "",
      category: item.issue_category || "",
      village: "", // backend doesn't return village name, only village_id
      mandalId: item.mandal_id ? String(item.mandal_id) : "", // pre-fill from the id we DO have
      date: item.visit_date || "",
      status: STATUS_TO_LABEL[item.status] || "Pending",
      issueDescription: item.issue_description || "",
      actionTaken: item.action_taken || "",
      document_url: item.document_url || null,
    });
    setEditFile(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditFile(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setEditFile(selected);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      let documentUrl = editItem.document_url;
      if (editFile) {
        const uploadRes = await fileAPI.upload(editFile, "janata_darbar");
        documentUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url ||
          documentUrl;
      }

      const resolvedMandalName = editItem.mandalId
        ? MANDALS.find((m) => String(m.id) === String(editItem.mandalId))?.name
        : undefined;

      await janataDarbarAPI.update(editItem.id, {
        visitor_name: editItem.name,
        mobile_number: editItem.mobile || null,
        epic_no: editItem.voterId || null,
        age: editItem.age ? Number(editItem.age) : null,
        gender: editItem.gender && editItem.gender !== "Select Gender" ? editItem.gender : null,
        issue_category: editItem.category,
        // village_name/mandal_name only sent if the user actually typed/picked
        // something new — otherwise we'd overwrite existing values with
        // empty strings, since the backend can't tell us the current names.
        village_name: editItem.village || undefined,
        mandal_name: resolvedMandalName,
        visit_date: editItem.date,
        status: STATUS_TO_BACKEND[editItem.status] ?? "pending",
        issue_description: editItem.issueDescription,
        action_taken: editItem.actionTaken || null,
        document_url: documentUrl,
      });

      await fetchVisitors();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update visitor:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- DELETE LOGIC ----------
  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await janataDarbarAPI.delete(deleteId);
      await fetchVisitors();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete visitor:", err);
      alert(err.response?.data?.detail || "Could not delete this visitor (super admin only).");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="darb123-page">
      {/* Header */}
      <div className="darb123-header">
        <div>
          <h2 className="darb123-title">Janata Darbar</h2>
          <p className="darb123-subtitle">
            Manage visitors who met during Janata Darbar.
          </p>
        </div>

        <div className="darb123-header-right">
          <div className="darb123-date-chip">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            {todayDisplay}
          </div>

          <button
            className="darb123-add-btn"
            onClick={() => navigate("/dashboard/JanataDarbarForm")}
          >
            <span className="darb123-add-icon">+</span> Add New Visitor
          </button>
        </div>
      </div>

      {loadError && <div className="darb123-no-data">{loadError}</div>}

      {/* Stat Cards */}
      <div className="darb123-stats-grid">
        <div className="darb123-stat-card darb123-stat-card-blue">
          <div className="darb123-stat-icon darb123-stat-icon-blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
              <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              <circle cx="17" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.7" />
              <path d="M15.5 14.7c2.6.4 4.5 2.3 4.5 5.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </div>
          <div className="darb123-stat-info">
            <span className="darb123-stat-label">Total Visitors</span>
            <span className="darb123-stat-value">{totalVisitors}</span>
          </div>
        </div>

        <div className="darb123-stat-card darb123-stat-card-green">
          <div className="darb123-stat-icon darb123-stat-icon-green">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="darb123-stat-info">
            <span className="darb123-stat-label">Resolved Cases</span>
            <span className="darb123-stat-value">{resolvedCount}</span>
          </div>
        </div>

        <div className="darb123-stat-card darb123-stat-card-purple">
          <div className="darb123-stat-icon darb123-stat-icon-purple">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="darb123-stat-info">
            <span className="darb123-stat-label">In Progress Cases</span>
            <span className="darb123-stat-value">{inProgressCount}</span>
          </div>
        </div>

        <div className="darb123-stat-card darb123-stat-card-orange">
          <div className="darb123-stat-icon darb123-stat-icon-orange">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 3h12M6 21h12M8 3c0 4 8 4 8 8s-8 4-8 8M16 3c0 4-8 4-8 8s8 4 8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="darb123-stat-info">
            <span className="darb123-stat-label">Pending Cases</span>
            <span className="darb123-stat-value">{pendingCount}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="darb123-filters">
        <div className="darb123-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by Name, Token or Mobile..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        <select
          className="darb123-select"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Categories</option>
          <option>Land Dispute</option>
          <option>Pension</option>
          <option>Financial Aid</option>
          <option>Employment</option>
          <option>Health</option>
          <option>Police Matter</option>
          <option>Personal</option>
          <option>Other</option>
        </select>

        <select
          className="darb123-select"
          value={mandalFilter}
          onChange={(e) => {
            setMandalFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option value="All Mandals">All Mandals</option>
          {MANDALS.map((m) => (
            <option key={m.id} value={String(m.id)}>
              {m.name}
            </option>
          ))}
        </select>

        <select
          className="darb123-select"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Resolved</option>
          <option>In Progress</option>
          <option>Pending</option>
          <option>Referred</option>
        </select>

        <button className="darb123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Clear Filters
        </button>
      </div>

      {/* Table */}
      <div className="darb123-table-wrapper">
        <table className="darb123-table">
          <thead>
            <tr>
              <th>Token No.</th>
              <th>Visitor Name</th>
              <th>Mobile</th>
              <th>Age</th>
              <th>Category</th>
              <th>Village</th>
              <th>Mandal</th>
              <th>Visit Date</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="darb123-no-data">Loading...</td>
              </tr>
            ) : pageVisitors.length === 0 ? (
              <tr>
                <td colSpan="10" className="darb123-no-data">
                  No visitors found.
                </td>
              </tr>
            ) : (
              pageVisitors.map((item, index) => {
                const statusLabel = STATUS_TO_LABEL[item.status] || item.status;
                const avatarBg = AVATAR_COLORS[index % AVATAR_COLORS.length];

                return (
                  <tr key={item.id}>
                    <td>
                      <span className="darb123-token-badge">{item.token_number}</span>
                    </td>

                    <td>
                      <div className="darb123-name-cell">
                        <div className={`darb123-avatar ${avatarBg}`}>
                          {getInitials(item.visitor_name)}
                        </div>
                        {item.visitor_name}
                      </div>
                    </td>

                    <td>
                      <div className="darb123-mobile-cell">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2.1L8 9.7a16 16 0 006 6l1.2-1.2a2 2 0 012.1-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                        </svg>
                        {item.mobile_number || "—"}
                      </div>
                    </td>

                    <td>{item.age ?? "—"}</td>

                    <td>
                      <span className="darb123-category-badge">{item.issue_category || "—"}</span>
                    </td>

                    <td>
                      <div className="darb123-place-cell">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z"
                            stroke="currentColor"
                            strokeWidth="1.7"
                            strokeLinejoin="round"
                          />
                          <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                        </svg>
                        {/* Backend only returns village_id, not the name */}
                        {item.village_id != null ? `Village #${item.village_id}` : "—"}
                      </div>
                    </td>

                    <td>
                      <div className="darb123-place-cell">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z"
                            stroke="currentColor"
                            strokeWidth="1.7"
                            strokeLinejoin="round"
                          />
                          <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                        </svg>
                        {mandalName(item.mandal_id)}
                      </div>
                    </td>

                    <td>
                      <div className="darb123-date-cell">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
                          <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                        </svg>
                        {formatDisplayDate(item.visit_date)}
                      </div>
                    </td>

                    <td>
                      <span className={`darb123-status-badge ${getStatusClass(statusLabel)}`}>
                        {statusLabel === "Resolved" && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
                            <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {statusLabel === "In Progress" && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
                            <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {statusLabel === "Pending" && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.2" />
                            <path d="M12 8v4l2.5 1.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                        {statusLabel}
                      </span>
                    </td>

                    <td>
                      <div className="darb123-action-cell">
                        <button
                          className="darb123-edit-btn"
                          onClick={() => openEditModal(item)}
                          title="Edit"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                              stroke="currentColor"
                              strokeWidth="1.9"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>

                        <button
                          className="darb123-delete-btn"
                          onClick={() => openDeleteModal(item.id)}
                          title="Delete"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                              stroke="currentColor"
                              strokeWidth="1.9"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="darb123-pagination-bar">
        <span className="darb123-pagination-info">
          Showing {pageVisitors.length === 0 ? 0 : startIndex + 1} to{" "}
          {startIndex + pageVisitors.length} of {filteredVisitors.length} entries
        </span>

        <div className="darb123-pagination-controls">
          <button
            className="darb123-page-arrow"
            onClick={() => handlePageChange(safePage - 1)}
          >
            &lt;
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`darb123-page-num ${safePage === p ? "darb123-active" : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <button
            className="darb123-page-arrow"
            onClick={() => handlePageChange(safePage + 1)}
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Note Footer */}
      <div className="darb123-note-bar">
        <div className="darb123-note-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
            <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <circle cx="17" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.7" />
            <path d="M15.5 14.7c2.6.4 4.5 2.3 4.5 5.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </div>
        <div>
          <strong>Note</strong>
          <p>Janata Darbar is held every Saturday from 10:00 AM to 2:00 PM.</p>
        </div>
      </div>

      {/* ---------------- EDIT MODAL ---------------- */}
      {showEditModal && editItem && (
        <div className="darb123-modal-overlay" onClick={closeEditModal}>
          <div className="darb123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="darb123-modal-header">
              <h3>Edit Visitor</h3>
              <button className="darb123-modal-close" onClick={closeEditModal}>
                &times;
              </button>
            </div>

            <form className="darb123-modal-body" onSubmit={handleEditSave}>
              <div className="darb123-form-row">
                <div className="darb123-form-group">
                  <label>Token No. (auto-generated)</label>
                  <input
                    type="text"
                    name="token"
                    value={editItem.token}
                    readOnly
                    disabled
                  />
                </div>

                <div className="darb123-form-group">
                  <label>Visitor Name</label>
                  <input
                    type="text"
                    name="name"
                    value={editItem.name}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              </div>

              <div className="darb123-form-row">
                <div className="darb123-form-group">
                  <label>Mobile</label>
                  <input
                    type="text"
                    name="mobile"
                    value={editItem.mobile}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="darb123-form-group">
                  <label>Age</label>
                  <input
                    type="number"
                    name="age"
                    value={editItem.age}
                    onChange={handleEditChange}
                  />
                </div>
              </div>

              <div className="darb123-form-row">
                <div className="darb123-form-group">
                  <label>Voter ID</label>
                  <input
                    type="text"
                    name="voterId"
                    value={editItem.voterId}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="darb123-form-group">
                  <label>Gender</label>
                  <select name="gender" value={editItem.gender} onChange={handleEditChange}>
                    <option>Select Gender</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="darb123-form-group">
                <label>Category</label>
                <select name="category" value={editItem.category} onChange={handleEditChange}>
                  <option>Land Dispute</option>
                  <option>Pension</option>
                  <option>Financial Aid</option>
                  <option>Employment</option>
                  <option>Health</option>
                  <option>Police Matter</option>
                  <option>Personal</option>
                  <option>Other</option>
                </select>
              </div>

              <div className="darb123-form-row">
                <div className="darb123-form-group">
                  <label>Village (leave blank to keep current)</label>
                  <input
                    type="text"
                    name="village"
                    placeholder="Only fill in to change"
                    value={editItem.village}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="darb123-form-group">
                  <label>Mandal</label>
                  <select name="mandalId" value={editItem.mandalId} onChange={handleEditChange}>
                    <option value="">Choose Mandal</option>
                    {MANDALS.map((m) => (
                      <option key={m.id} value={String(m.id)}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="darb123-form-row">
                <div className="darb123-form-group">
                  <label>Visit Date</label>
                  <input
                    type="date"
                    name="date"
                    value={editItem.date}
                    onChange={handleEditChange}
                    required
                  />
                </div>

                <div className="darb123-form-group">
                  <label>Status</label>
                  <select name="status" value={editItem.status} onChange={handleEditChange}>
                    <option>Pending</option>
                    <option>In Progress</option>
                    <option>Resolved</option>
                    <option>Referred</option>
                  </select>
                </div>
              </div>

              <div className="darb123-form-group">
                <label>Replace Photo / Document (optional)</label>
                <input type="file" accept="image/*,.pdf" onChange={handleEditFileChange} />
              </div>

              <div className="darb123-form-group">
                <label>Issue Description</label>
                <textarea
                  name="issueDescription"
                  rows="3"
                  value={editItem.issueDescription}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="darb123-form-group">
                <label>Action Taken</label>
                <textarea
                  name="actionTaken"
                  rows="3"
                  value={editItem.actionTaken}
                  onChange={handleEditChange}
                />
              </div>

              <div className="darb123-modal-footer">
                <button
                  type="button"
                  className="darb123-btn-cancel"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  Cancel
                </button>
                <button type="submit" className="darb123-btn-save" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- DELETE CONFIRM MODAL ---------------- */}
      {showDeleteModal && (
        <div className="darb123-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="darb123-modal darb123-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="darb123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 className="darb123-confirm-title">Delete this visitor record?</h3>
            <p className="darb123-confirm-text">
              This action cannot be undone. Are you sure you want to delete
              this visitor entry?
            </p>

            <div className="darb123-confirm-footer">
              <button className="darb123-btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button className="darb123-btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JanataDarbarTable;